import { useEffect, useMemo, useState } from "react";
import { Modal } from "src/components/DialogComponent";
import moment from "moment";
import { siteUrl } from "src/api/config";
import { InfoIcon } from "src/components/ui-icons/myTasks/info";
import { TooltipItem } from "src/utils/Tooltip";

/** Картинка из `public/sdca/` — всегда с текущего origin (важно для PWA / mobile, где внешний CDN может быть в CSP или отдавать 401). */
function sameOriginSdcaImagePath(): string {
    const pub = (process.env.PUBLIC_URL || "").toString().trim().replace(/\s+/g, "");
    if (pub.startsWith("http")) {
        return "/sdca/sdca_image.png";
    }
    const prefix = pub.endsWith("/") ? pub.slice(0, -1) : pub;
    return `${prefix}/sdca/sdca_image.png`.replace(/([^:])\/{2,}/g, "$1");
}

const SDCA_FALLBACK_SVG_DATA_URI = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="720" height="420" viewBox="0 0 720 420">
  <rect width="100%" height="100%" fill="#F8FAFC"/>
  <text x="360" y="40" text-anchor="middle" font-family="system-ui,sans-serif" font-size="20" font-weight="600" fill="#012169">SDCA</text>
  <text x="360" y="66" text-anchor="middle" font-family="system-ui,sans-serif" font-size="12" fill="#637381">Standardize — Do — Check — Act</text>
  <text x="40" y="110" font-family="system-ui,sans-serif" font-size="13" fill="#111928">S — стандартизировать действия и правила.</text>
  <text x="40" y="145" font-family="system-ui,sans-serif" font-size="13" fill="#111928">D — выполнить работу по стандарту.</text>
  <text x="40" y="180" font-family="system-ui,sans-serif" font-size="13" fill="#111928">C — сравнить результат с ожиданиями и данными.</text>
  <text x="40" y="215" font-family="system-ui,sans-serif" font-size="13" fill="#111928">A — скорректировать процесс при отклонениях.</text>
  <text x="40" y="270" font-family="system-ui,sans-serif" font-size="11" fill="#637381">Полная схема: внешний URL или файл public/sdca/sdca_image.png на этом домене.</text>
</svg>`
)}`;

const InfoIconSdca = () => {
    const [open, setOpen] = useState(false);
    const [imageIndex, setImageIndex] = useState(0);
    const [isImageBroken, setIsImageBroken] = useState(false);
    const [zoom, setZoom] = useState(1);
    const [isMobile, setIsMobile] = useState(false);
    const imageUrls = useMemo(() => {
        const local =
            typeof window !== "undefined"
                ? `${window.location.origin}${sameOriginSdcaImagePath()}`
                : sameOriginSdcaImagePath();
        return [
            "https://supply.napitkivmeste.ru/supply-strapi-uploads/sdca_image_1771321182_8e5485b8cc_a4211234b6.png?v=1772102560",
            local,
            SDCA_FALLBACK_SVG_DATA_URI,
        ];
    }, []);
    const currentImageUrl = imageUrls[imageIndex];
    const minZoom = 1;
    const maxZoom = 6;
    const zoomStep = 0.5;
    const zoomPercent = Math.round(zoom * 100);

    useEffect(() => {
        if (typeof window === "undefined") return;
        const mql = window.matchMedia("(max-width: 639px)");
        const update = () => setIsMobile(mql.matches);
        update();
        if (typeof mql.addEventListener === "function") {
            mql.addEventListener("change", update);
            return () => mql.removeEventListener("change", update);
        }
        mql.addListener(update);
        return () => mql.removeListener(update);
    }, []);

    return (
        <>
            <TooltipItem
                wrapperClassName="w-[230px]"
                position="top-right"
                tooltipsText=""
                openOnMobileTap
                tooltipsReactNode={
                    <button
                        type="button"
                        className="text-[#012169] text-sm cursor-pointer border-b border-dotted border-[#012169]"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setImageIndex(0);
                            setIsImageBroken(false);
                            setZoom(1);
                            setOpen(true);
                        }}
                    >
                        Узнать подробнее про SDCA
                    </button>
                }
            >
                <span
                    className="inline-flex"
                    onClick={(e) => {
                        // Prevent accidental navigation/parent click handlers; tooltip should be the primary interaction.
                        e.preventDefault();
                        e.stopPropagation();
                    }}
                >
                    <InfoIcon />
                </span>
            </TooltipItem>

            <Modal
                open={open}
                toggleDialog={setOpen}
                zIndex={120}
                modalClassName="p-0 w-full sm:min-w-[600px] max-w-[calc(100vw-5%)] max-h-[calc(100dvh-80px)] overflow-hidden flex flex-col"
                closeOnBackdrop
            >
                <header className="px-6 pt-6 pb-4 border-b">
                    <h2 className="text-base font-medium text-black">SDCA</h2>
                </header>

                <div className="px-6 py-4 flex-1 overflow-hidden flex flex-col">
                    {!isImageBroken && isMobile && (
                        <div className="flex items-center justify-end gap-2 pb-3">
                            <span className="text-xs text-[#637381] mr-1 tabular-nums">{zoomPercent}%</span>
                            <button
                                type="button"
                                className="h-8 px-3 rounded border text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={() => setZoom((prev) => Math.max(minZoom, Math.round((prev - zoomStep) * 100) / 100))}
                                disabled={zoom <= minZoom}
                            >
                                −
                            </button>
                            <button
                                type="button"
                                className="h-8 px-3 rounded border text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={() => setZoom((prev) => Math.min(maxZoom, Math.round((prev + zoomStep) * 100) / 100))}
                                disabled={zoom >= maxZoom}
                            >
                                +
                            </button>
                            <button
                                type="button"
                                className="h-8 px-3 rounded border text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={() => setZoom(1)}
                                disabled={zoom === 1}
                            >
                                Сброс
                            </button>
                        </div>
                    )}
                    <div className="flex-1 overflow-auto">
                        {!isImageBroken && (
                            <div className="max-w-none" style={{ width: `${(isMobile ? zoom : 1) * 100}%` }}>
                                <img
                                    src={currentImageUrl}
                                    alt="SDCA"
                                    className="w-full h-auto select-none block max-w-none"
                                    loading="eager"
                                    referrerPolicy="no-referrer"
                                    onLoad={() => setIsImageBroken(false)}
                                    onError={() => {
                                        if (imageIndex < imageUrls.length - 1) {
                                            setZoom(1);
                                            setImageIndex((prev) => prev + 1);
                                            return;
                                        }
                                        setIsImageBroken(true);
                                    }}
                                />
                            </div>
                        )}
                        {isImageBroken && (
                            <div className="text-sm text-[#637381]">
                                Не удалось загрузить изображение SDCA (внешний адрес может быть недоступен в приложении из‑за политики безопасности или авторизации).
                                Добавьте копию схемы в{" "}
                                <code className="text-xs bg-gray-100 px-1 rounded">public/sdca/sdca_image.png</code> — она подгрузится с того же домена, что и приложение.
                            </div>
                        )}
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default InfoIconSdca;
